import { Menu, Skeleton, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useApiTestUserCredentialsConnection } from '@repo/api-codegen';
import { SettingsButton } from '@repo/common/components';
import { integrationList } from '@repo/common/constants/integration/integrations';
import { Banner, Button } from '@repo/foundations';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { IIntegration } from '../../../api';
import { useIntegrationList } from '../../../api';
import { apiClient } from '../../../api/common/axios';
import IntegrationLogo from '../../IntegrationLogo';
import { openModal } from '../../ModalManager';
import { UserLevelCredentialsModal } from './UserLevelCredentialsModal';

interface IntegrationCredentialStatus {
	integration: IIntegration;
	hasCredentials: boolean;
	isTestingConnection: boolean;
	connectionStatus: 'success' | 'error' | 'unknown';
	connectionError?: string;
	isCheckingCredentials: boolean;
}

interface IntegrationOauthStatusResponse {
	connected: boolean;
}

export function UserLevelCredentialsSettings() {
	const { data: integrations, isLoading: isLoadingIntegrations } =
		useIntegrationList({});
	const [statuses, setStatuses] = useState<
		Record<string, IntegrationCredentialStatus>
	>({});
	const { mutateAsync: testUserCredentials } =
		useApiTestUserCredentialsConnection({});

	// Filter integrations that have user-level credentials or OAuth enabled
	const userLevelCredentialsIntegrations = useMemo(() => {
		if (!integrations?.results) return [];

		return integrations.results.filter((integration) => {
			const credentials = (integration.credentials ?? {}) as Record<
				string,
				unknown
			>;
			const isOauthEnabled = Boolean(credentials.oauth_enabled);
			return (
				integration.query_execute_use_user_level_credentials || isOauthEnabled
			);
		});
	}, [integrations?.results]);

	const checkCredentials = useCallback(
		async (integrationId: string) => {
			setStatuses((prev) => ({
				...prev,
				[integrationId]: {
					...prev[integrationId],
					isCheckingCredentials: true,
				},
			}));

			try {
				const result = await testUserCredentials({
					pathParams: { integrationId },
				});

				setStatuses((prev) => ({
					...prev,
					[integrationId]: {
						...prev[integrationId],
						hasCredentials: true,
						isCheckingCredentials: false,
						connectionStatus: result.success ? 'success' : 'error',
						connectionError: result.success
							? undefined
							: result.error || 'Connection failed',
					},
				}));
			} catch (error: unknown) {
				const err = error as { message?: string; status?: number };
				const isCredentialsNotConfigured =
					err?.message?.includes('not configured') ||
					err?.message?.includes('personal credentials') ||
					err?.status === 400;

				setStatuses((prev) => ({
					...prev,
					[integrationId]: {
						...prev[integrationId],
						hasCredentials: !isCredentialsNotConfigured,
						isCheckingCredentials: false,
						connectionStatus: isCredentialsNotConfigured ? 'unknown' : 'error',
						connectionError: isCredentialsNotConfigured
							? undefined
							: err?.message || 'Connection failed',
					},
				}));
			}
		},
		[testUserCredentials]
	);

	const handleConfigureCredentials = useCallback(
		(integration: IIntegration) => {
			const spec = integrationList.find(
				(spec) => spec.type === integration.type
			);
			if (!spec?.userLevelCredentialsFields?.length) {
				showNotification({
					title: 'Not supported',
					message: 'This integration does not support user-level credentials',
					color: 'orange',
				});
				return;
			}

			openModal({
				title: integration.name,
				children: (
					<UserLevelCredentialsModal
						integration={integration}
						onCredentialsSaved={() => {
							checkCredentials(integration.id);
						}}
					/>
				),
				size: 'md',
			});
		},
		[checkCredentials]
	);

	useEffect(() => {
		if (!userLevelCredentialsIntegrations.length) return;

		const initialStatuses: Record<string, IntegrationCredentialStatus> = {};

		userLevelCredentialsIntegrations.forEach((integration) => {
			if (!statuses[integration.id]) {
				initialStatuses[integration.id] = {
					integration,
					hasCredentials: false,
					isTestingConnection: false,
					connectionStatus: 'unknown',
					isCheckingCredentials: false,
				};
			}
		});

		if (Object.keys(initialStatuses).length > 0) {
			setStatuses((prev) => ({ ...prev, ...initialStatuses }));

			Object.keys(initialStatuses).forEach((integrationId) => {
				checkCredentials(integrationId);
			});
		}
	}, [userLevelCredentialsIntegrations, checkCredentials]);

	if (isLoadingIntegrations) {
		return (
			<Stack spacing="md">
				{[1, 2, 3].map((i) => (
					<Skeleton key={i} height={60} />
				))}
			</Stack>
		);
	}

	if (!userLevelCredentialsIntegrations.length) {
		return (
			<Banner
				tone="info"
				title="No integrations found"
				message="No integrations have user-level credentials enabled. Enable this feature in the integration preferences to require users to provide their own credentials."
			/>
		);
	}

	return (
		<Stack spacing={0}>
			{userLevelCredentialsIntegrations.map((integration) => {
				const status = statuses[integration.id];
				const credentials = (integration.credentials ?? {}) as Record<
					string,
					unknown
				>;
				const isOauthEnabled = Boolean(credentials.oauth_enabled);

				return (
					<IntegrationCredentialRow
						key={integration.id}
						integration={integration}
						status={status}
						isOauthEnabled={isOauthEnabled}
						onConfigureCredentials={handleConfigureCredentials}
						onRefreshStatus={() => checkCredentials(integration.id)}
					/>
				);
			})}
		</Stack>
	);
}

interface IntegrationCredentialRowProps {
	integration: IIntegration;
	status: IntegrationCredentialStatus | undefined;
	isOauthEnabled: boolean;
	onConfigureCredentials: (integration: IIntegration) => void;
	onRefreshStatus: () => void;
}

function IntegrationCredentialRow({
	integration,
	status,
	isOauthEnabled,
	onConfigureCredentials,
	onRefreshStatus,
}: IntegrationCredentialRowProps) {
	// OAuth status query
	const statusQueryKey = [
		'integration-oauth-status',
		integration.id,
		integration.type,
	] as const;
	const {
		data: oauthStatus,
		isLoading: isOauthLoading,
		error: oauthError,
	} = useQuery<IntegrationOauthStatusResponse>({
		queryKey: statusQueryKey,
		queryFn: async (): Promise<IntegrationOauthStatusResponse> => {
			const response = await apiClient.get(
				`/integration_v2/integrations/${integration.id}/${integration.type}/oauth/status/`
			);
			return response.data as IntegrationOauthStatusResponse;
		},
		enabled: isOauthEnabled,
		retry: false,
	});

	const isOauthConnected = oauthStatus?.connected ?? false;

	const handleOauthConnect = async () => {
		try {
			const response = await apiClient.get(
				`/integration_v2/integrations/${integration.id}/${integration.type}/oauth/initiate/`,
				{ params: { return_url: window.location.pathname } }
			);
			const data = response.data as { authorization_url: string };
			if (data.authorization_url) {
				window.location.href = data.authorization_url;
			} else {
				showNotification({
					title: 'Error',
					message: 'Failed to get authorization URL',
					color: 'red',
				});
			}
		} catch {
			showNotification({
				title: 'Error',
				message: 'Failed to initiate OAuth connection',
				color: 'red',
			});
		}
	};

	const handleOauthDisconnect = async () => {
		await apiClient.delete(
			`/integration_v2/integrations/${integration.id}/${integration.type}/oauth/disconnect/`
		);
		onRefreshStatus();
	};

	const isLoading =
		status?.isCheckingCredentials ||
		status?.isTestingConnection ||
		isOauthLoading;

	// Determine connection state
	const isConnected = isOauthEnabled
		? isOauthConnected
		: status?.connectionStatus === 'success';

	const renderButton = () => {
		if (isLoading) {
			return (
				<Button variant="tertiary" disabled>
					Checking...
				</Button>
			);
		}

		if (isConnected) {
			return (
				<Menu position="bottom-end">
					<Menu.Target>
						<Button
							variant="tertiary"
							leftIconName="pointFilled"
							rightIconName="chevronDown"
							leftIconFilled
						>
							Connected
						</Button>
					</Menu.Target>
					<Menu.Dropdown>
						{isOauthEnabled ? (
							<Menu.Item onClick={() => void handleOauthDisconnect()}>
								Disconnect
							</Menu.Item>
						) : (
							<>
								<Menu.Item onClick={() => onConfigureCredentials(integration)}>
									Edit credentials
								</Menu.Item>
								<Menu.Item onClick={onRefreshStatus}>Test connection</Menu.Item>
							</>
						)}
					</Menu.Dropdown>
				</Menu>
			);
		}

		return (
			<Button
				variant="default"
				onClick={() =>
					isOauthEnabled
						? void handleOauthConnect()
						: onConfigureCredentials(integration)
				}
				rightIconName={isOauthEnabled ? 'arrowUpRight' : undefined}
			>
				Connect
			</Button>
		);
	};

	return (
		<SettingsButton
			title={integration.name}
			leftComponent={
				<IntegrationLogo
					integrationType={integration.type}
					integrationId={integration.id}
					size={24}
				/>
			}
		>
			{renderButton()}
		</SettingsButton>
	);
}
